import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { Question } from '../types/database'
import { QuestionForm } from './QuestionForm'
import { Sidebar } from './Sidebar'
import { useAuth } from '../contexts/AuthContext'

type QuestionWithRelations = Question & {
  question_groups: {
    name: string
    book_id: number
    books: {
      name: string
      code: string | null
    }
  } | null
}

export function QuestionList() {
  const { roles } = useAuth()
  const [questions, setQuestions] = useState<QuestionWithRelations[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [downloadingId, setDownloadingId] = useState<number | null>(null)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(20)
  const [totalCount, setTotalCount] = useState(0)

  const fetchQuestions = async () => {
    try {
      setLoading(true)
      let query = supabase
        .from('questions')
        .select(`
          *,
          question_groups (
            name,
            book_id,
            books (
              name,
              code
            )
          )
        `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range((currentPage - 1) * itemsPerPage, (currentPage - 1) * itemsPerPage + itemsPerPage - 1)
      if (!roles) {
        query = query.lt('status', 0)
      } else {
        query = query.gte('status', 0)
      }
      const { data, error, count } = await query
      if (error) throw error
      setQuestions(data || [])
      setTotalCount(count || 0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch questions')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchQuestions()
  }, [currentPage, itemsPerPage])

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])

  const handleDelete = async (id: number) => {
    if (!confirm('정말 삭제하시겠습니까?')) return

    try {
      const question = questions.find(q => q.id === id)

      // Delete file from storage if exists
      if (question?.file) {
        const bookCode = question.question_groups?.books?.code
        const filePath = bookCode ? `${bookCode}/${question.file}` : question.file
        await supabase.storage.from('files').remove([filePath])
      }

      const { error } = await supabase
        .from('questions')
        .delete()
        .eq('id', id)

      if (error) throw error
      await fetchQuestions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete question')
    }
  }

  const handleEdit = (question: Question) => {
    setEditingQuestion(question)
    setShowForm(true)
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingQuestion(null)
    fetchQuestions()
  }

  const getFileUrl = async (filePath: string | null, bookCode: string | null): Promise<string | null> => {
    if (!filePath) return null

    try {
      // Construct the full path: {book.code}/{question.file}
      const fullPath = bookCode ? `${bookCode}/${filePath}` : filePath

      const { data, error } = await supabase.storage
        .from('files')
        .createSignedUrl(fullPath, 3600) // URL valid for 1 hour

      if (error) throw error
      return data.signedUrl
    } catch (err) {
      console.error('Failed to get file URL:', err)
      return null
    }
  }

  const handleDownload = async (filePath: string | null, questionId: number) => {
    if (!filePath) return

    setDownloadingId(questionId)
    try {
      const question = questions.find(q => q.id === questionId)
      const bookCode = question?.question_groups?.books?.code || null
      const url = await getFileUrl(filePath, bookCode)
      if (url) {
        window.open(url, '_blank')
      } else {
        setError('파일을 다운로드할 수 없습니다.')
      }
    } catch (err) {
      setError('파일 다운로드 중 오류가 발생했습니다.')
      console.error('Download error:', err)
    } finally {
      setDownloadingId(null)
    }
  }

  // No need to filter on client side for search if we are doing server side filtering/pagination.
  // BUT the current implementation does client side filtering for search.
  // To match Prism: Prism does client-side search filtering on the fetched page OR server side search?
  // Checking Prism code:
  // Prism code:
  // const filteredQuestions = questions.filter((q) => ...)
  // This means Prism applies search filter ONLY on the current page fetched from server?
  // Let's re-read Prism code carefully.
  // Prism fetchQuestions has NO search query filter in Supabase query.
  // So Prism fetches a page, THEN filters it by name? That seems wrong for pagination + search.
  // BUT, looking at Prism Code provided in Step 103:
  // It fetches a range. AND THEN filters.
  // This implies search only works on the CURRENT PAGE. This is a known issue/behavior in Prism if confirmed.
  // Wait, I see no `searchQuery` usage in `fetchQuestions` in Prism code.
  // So yes, search is only on the current page. I will replicate this behavior as instructed ("prism 참고").

  const filteredQuestions = questions.filter((q) =>
    q.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    q.question_groups?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (loading) {
    return (
      <div className="relative flex min-h-screen w-full bg-background-light font-display">
        <Sidebar />
        <main className="ml-64 flex flex-1 items-center justify-center">
          <div className="flex items-center gap-2 text-gray-600">
            <span className="material-symbols-outlined animate-spin">progress_activity</span>
            <span className="text-lg">로딩 중...</span>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="relative flex min-h-screen w-full bg-background-light font-display">
      <Sidebar />
      <main className="ml-64 flex flex-1 flex-col p-6 lg:p-8">
        <div className="w-full max-w-7xl mx-auto">
          <header className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">문제 관리</h1>
            {roles.includes('moderator') && (
              <button
                onClick={() => setShowForm(true)}
                className="flex min-w-[84px] items-center justify-center gap-2 rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold leading-normal tracking-wide hover:bg-primary/90 transition-colors"
              >
                <span className="material-symbols-outlined">add</span>
                <span className="truncate">새 문제 등록</span>
              </button>
            )}
          </header>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-base">error</span>
              <span className="text-sm">{error}</span>
            </div>
          )}

          {showForm && (
            <QuestionForm
              question={editingQuestion}
              onClose={handleFormClose}
            />
          )}

          <div className="mb-6 flex flex-wrap items-center gap-4">
            <div className="flex w-full flex-1 items-stretch rounded-lg h-10 bg-white border border-gray-200 focus-within:ring-2 focus-within:ring-primary lg:w-auto lg:flex-1">
              <div className="text-gray-400 flex items-center justify-center pl-3">
                <span className="material-symbols-outlined">search</span>
              </div>
              <input
                className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-r-lg text-gray-900 focus:outline-0 focus:ring-0 border-none bg-transparent h-full placeholder:text-gray-400 px-4 pl-2 text-sm"
                placeholder="문제 이름으로 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            {filteredQuestions.length === 0 ? (
              <div className="text-center py-12">
                <span className="material-symbols-outlined text-gray-300 text-6xl mb-4">inbox</span>
                <p className="text-gray-500">
                  {searchQuery ? '검색 결과가 없습니다.' : '등록된 문제가 없습니다.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-max">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        문제 이름
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        책
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        문제 그룹
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        파일
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        등록일
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        작업
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredQuestions.map((question) => (
                      <tr key={question.id} className="hover:bg-gray-50 transition-colors">
                        <td className="h-[72px] px-4 py-2 text-sm font-medium text-gray-800">
                          {question.name}
                        </td>
                        <td className="h-[72px] px-4 py-2 text-sm text-gray-600">
                          {question.question_groups?.books?.name || (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="h-[72px] px-4 py-2 text-sm text-gray-600">
                          {question.question_groups?.name || (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="h-[72px] px-4 py-2">
                          {question.file ? (
                            <button
                              onClick={() => handleDownload(question.file, question.id)}
                              disabled={downloadingId === question.id}
                              className="inline-flex items-center gap-1 text-sm text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                            >
                              {downloadingId === question.id ? (
                                <>
                                  <span className="material-symbols-outlined text-base animate-spin">progress_activity</span>
                                  <span>다운로드 중...</span>
                                </>
                              ) : (
                                <>
                                  <span className="material-symbols-outlined text-base">attach_file</span>
                                  <span>첨부파일</span>
                                </>
                              )}
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="h-[72px] px-4 py-2 text-sm text-gray-500">
                          {new Date(question.created_at).toLocaleDateString('ko-KR', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </td>
                        <td className="h-[72px] px-4 py-2">
                          <div className="flex items-center gap-2">
                            {roles.includes('moderator') && (
                              <button
                                onClick={() => handleEdit(question)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-yellow-700 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
                              >
                                <span className="material-symbols-outlined text-sm">edit</span>
                                <span>수정</span>
                              </button>
                            )}
                            {roles.includes('moderator') && (
                              <button
                                onClick={() => handleDelete(question.id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                              >
                                <span className="material-symbols-outlined text-sm">delete</span>
                                <span>삭제</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {filteredQuestions.length > 0 && (
            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {/* Results summary */}
              <div className="text-sm text-gray-600">
                전체 {totalCount}개 중 {Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)}-
                {Math.min(currentPage * itemsPerPage, totalCount)}개 표시
              </div>

              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                {/* Items per page selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">페이지당:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value))
                      setCurrentPage(1) // Reset to first page when changing items per page
                    }}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value={10}>10개</option>
                    <option value={20}>20개</option>
                    <option value={50}>50개</option>
                    <option value={100}>100개</option>
                  </select>
                </div>

                {/* Page navigation */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
                  >
                    <span className="material-symbols-outlined text-sm">chevron_left</span>
                    <span>이전</span>
                  </button>

                  <div className="flex items-center gap-1">
                    {(() => {
                      const totalPages = Math.ceil(totalCount / itemsPerPage)
                      const pages: (number | string)[] = []

                      if (totalPages <= 7) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i)
                        }
                      } else {
                        if (currentPage <= 3) {
                          pages.push(1, 2, 3, 4, '...', totalPages)
                        } else if (currentPage >= totalPages - 2) {
                          pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
                        } else {
                          pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages)
                        }
                      }

                      return pages.map((page, idx) => (
                        page === '...' ? (
                          <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">...</span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page as number)}
                            className={`min-w-[32px] rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${currentPage === page
                              ? 'bg-primary text-white'
                              : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        )
                      ))
                    })()}
                  </div>

                  <button
                    onClick={() => setCurrentPage(Math.min(Math.ceil(totalCount / itemsPerPage), currentPage + 1))}
                    disabled={currentPage >= Math.ceil(totalCount / itemsPerPage)}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
                  >
                    <span>다음</span>
                    <span className="material-symbols-outlined text-sm">chevron_right</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
